import { cva } from "class-variance-authority";

/** Previous and next month: 32px round icon buttons, 44px in a bottom sheet. */
export const navigationButtonCva = cva(
  "inline-flex items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-700 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-2 focus-visible:outline-primary-500 dark:text-gray-300 dark:hover:bg-dark-700",
  {
    variants: {
      sheet: {
        true: "size-11",
        false: "size-8",
      },
    },
    defaultVariants: {
      sheet: false,
    },
  },
);

/**
 * A day cell. The cell carries the band of a period (full, or half at its start and end); the round inside it
 * carries today, the selection and the keyboard focus.
 *
 * Only the band sets the background colour: Tailwind 4 orders utilities of the same property by name, so a
 * `bg-transparent` in the base would win over `bg-primary-50` and hide the band between start and end.
 */
export const calendarDayCva = cva(
  "group relative flex items-center justify-center border-0 p-0 tabular-nums focus-visible:outline-hidden",
  {
    variants: {
      sheet: {
        true: "h-[46px] text-base",
        false: "h-9 text-sm",
      },
      isCurrentMonth: {
        true: "text-gray-800 dark:text-gray-200",
        false: "text-gray-400 dark:text-dark-400",
      },
      isDisabled: {
        true: "cursor-not-allowed text-[#C7C7C7] dark:text-dark-500",
        false: "cursor-pointer",
      },
      band: {
        none: "bg-transparent",
        full: "bg-primary-50 dark:bg-primary-900/25",
        start: "bg-transparent bg-[linear-gradient(90deg,transparent_50%,#E8F5E9_50%)] dark:bg-[linear-gradient(90deg,transparent_50%,rgb(46_125_50/0.25)_50%)]",
        end: "bg-transparent bg-[linear-gradient(90deg,#E8F5E9_50%,transparent_50%)] dark:bg-[linear-gradient(90deg,rgb(46_125_50/0.25)_50%,transparent_50%)]",
        previewFull: "bg-[#F1F8E9] dark:bg-primary-900/15",
        previewStart: "bg-transparent bg-[linear-gradient(90deg,transparent_50%,#F1F8E9_50%)] dark:bg-[linear-gradient(90deg,transparent_50%,rgb(46_125_50/0.15)_50%)]",
        previewEnd: "bg-transparent bg-[linear-gradient(90deg,#F1F8E9_50%,transparent_50%)] dark:bg-[linear-gradient(90deg,rgb(46_125_50/0.15)_50%,transparent_50%)]",
      },
    },
    defaultVariants: {
      sheet: false,
      isCurrentMonth: true,
      isDisabled: false,
      band: "none",
    },
  },
);

export const calendarDayInnerCva = cva(
  "inline-flex items-center justify-center rounded-full transition-colors",
  {
    variants: {
      sheet: {
        true: "size-[42px]",
        false: "size-[34px]",
      },
      state: {
        idle: "group-enabled:group-hover:bg-gray-100 dark:group-enabled:group-hover:bg-dark-700",
        today: "font-medium text-primary-900 shadow-[inset_0_0_0_1px] shadow-primary-600 group-enabled:group-hover:bg-gray-100 dark:text-primary-300 dark:group-enabled:group-hover:bg-dark-700",
        selected: "bg-primary-600 font-medium text-white",
        previewEnd: "bg-white text-primary-900 shadow-[inset_0_0_0_2px] shadow-primary-600 dark:bg-dark-800 dark:text-primary-300",
      },
      isDisabled: {
        true: "line-through decoration-[#D6D6D6]",
        false: "",
      },
      isFocused: {
        true: "ring-2 ring-primary-500",
        false: "group-focus-visible:ring-2 group-focus-visible:ring-primary-500",
      },
    },
    defaultVariants: {
      sheet: false,
      state: "idle",
      isDisabled: false,
      isFocused: false,
    },
  },
);

/** A quick choice of a period: a compact one-line row. */
export const rangePresetCva = cva(
  "flex w-full items-center rounded-full border-0 px-2.5 py-1 text-left text-[13px] leading-5 focus-visible:outline-2 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:opacity-40",
  {
    variants: {
      active: {
        true: "bg-primary-50 font-medium text-primary-900 dark:bg-primary-900/30 dark:text-primary-300",
        false: "bg-transparent text-gray-800 enabled:hover:bg-gray-100 dark:text-gray-200 dark:enabled:hover:bg-dark-700",
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

/** The time in the field: "+ Tijd" while there is none, dashed; solid green while its picker is open. */
export const addTimeChipCva = cva(
  "mr-1 inline-flex h-6 shrink-0 cursor-pointer items-center gap-1 rounded-full border bg-white py-0 pr-2.5 pl-2 text-xs leading-4 font-medium whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500 dark:bg-dark-800 max-sm:h-8",
  {
    variants: {
      state: {
        idle: "border-dashed border-gray-400 text-gray-700 hover:border-primary-600 hover:bg-[#F1F8E9] hover:text-primary-900 dark:text-gray-300 dark:hover:bg-dark-700",
        active: "border-solid border-primary-600 bg-[#F1F8E9] text-primary-900 dark:bg-dark-700 dark:text-primary-300",
        error: "border-dashed border-danger-600 text-danger-800 dark:text-danger-400",
      },
    },
    defaultVariants: {
      state: "idle",
    },
  },
);

export const timeChipCva = cva(
  "mr-1 inline-flex h-6 shrink-0 items-center overflow-hidden rounded-full border border-primary-100 bg-primary-50 text-primary-900 dark:border-primary-900 dark:bg-primary-900/30 dark:text-primary-300 max-sm:h-8",
  {
    variants: {
      active: {
        true: "shadow-[0_0_0_2px] shadow-primary-200",
        false: "",
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);
